import React, { useState } from "react";
import { Box, Typography, InputBase, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useAuth from "../../../../../../hooks/useAuth";
import { ROLES } from "../../../../../../constants/auth";
import { approveRequest, rejectRequest } from "../../../../../../api/approvals";

const ActionFooter = ({ request, onClose, onActionDone }) => {
  const theme  = useTheme();
  const t      = theme.custom;
  const isDark = theme.palette.mode === "dark";
  const { user } = useAuth();

  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectComment,  setRejectComment]  = useState("");
  const [actioning,      setActioning]      = useState(false);
  const [actionError,    setActionError]    = useState(null);

  // Role-aware action gate — from API doc section 5.5:
  // PMO  can approve/reject only when status = SUBMITTED  (Gate 1)
  // SCG  can approve/reject only when status = PMO_APPROVED (Gate 2)
  const role    = user?.role;
  const isPMO   = role === ROLES.PMO;
  const isSCG   = role === ROLES.SCG;

  const canAct =
    (isPMO && (request.status === "SUBMITTED" || request.status === "MOVE_PENDING")) ||
    (isSCG && request.status === "PMO_APPROVED");

  // Explain why actions are hidden — shown as read-only info strip
  const gateMessage = (() => {
    if (isPMO && request.status === "PMO_APPROVED")
      return "This request is at SCG Gate 2 — awaiting SCG review.";
    if (isSCG && request.status === "SUBMITTED")
      return "This request is at PMO Gate 1 — awaiting PMO review.";
    if (request.status === "ACTIVE")
      return "This request has been approved and is now active.";
    if (request.status === "ENDED")
      return "This allocation has ended.";
    if (request.status === "REJECTED")
      return "This request has been rejected.";
    return null;
  })();

  const approveLabel = isPMO
    ? "✓ Approve & Forward to SCG"
    : "✓ Approve & Activate";

  const handleApprove = async () => {
    try {
      setActioning(true);
      setActionError(null);
      await approveRequest(request.requestId);
      onActionDone?.();
    } catch (err) {
      setActionError(err.response?.data?.error || "Approval failed. Please try again.");
    } finally {
      setActioning(false);
    }
  };

  const handleReject = async () => {
    if (!rejectComment.trim()) { setActionError("Rejection reason is required."); return; }
    try {
      setActioning(true);
      setActionError(null);
      await rejectRequest(request.requestId, rejectComment.trim());
      onActionDone?.();
    } catch (err) {
      setActionError(err.response?.data?.error || "Rejection failed. Please try again.");
    } finally {
      setActioning(false);
    }
  };

  return (
    <Box sx={{ borderTop: `1px solid ${t.border2}`, flexShrink: 0 }}>

      {/* Gate message — shown when current role can't act on this request */}
      {!canAct && gateMessage && (
        <Box sx={{
          padding: "8px 20px",
          background: t.surface2,
          borderBottom: `1px solid ${t.border}`,
        }}>
          <Typography sx={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "10px", color: t.text3,
            letterSpacing: "0.04em",
          }}>
            {gateMessage}
          </Typography>
        </Box>
      )}

      {/* Reject form */}
      {showRejectForm && (
        <Box sx={{ padding: "12px 20px", borderBottom: `1px solid ${t.border}` }}>
          <Typography sx={{
            fontFamily: "'DM Mono', monospace", fontSize: "8px",
            letterSpacing: "0.15em", textTransform: "uppercase",
            color: t.text3, mb: "5px",
          }}>
            Rejection Reason <span style={{ color: "#c84040" }}>*</span>
          </Typography>
          <Box sx={{
            background: t.surface2,
            border: "1px solid rgba(200,64,64,0.4)",
            borderRadius: "3px", padding: "7px 10px",
            "&:focus-within": { borderColor: "#c84040" },
          }}>
            <InputBase
              multiline rows={3}
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              placeholder="Provide reason for rejection..."
              sx={{
                width: "100%", fontFamily: "'Inter', sans-serif",
                fontSize: "12px", color: t.text,
                "& textarea::placeholder": { color: t.text3 },
              }}
            />
          </Box>
        </Box>
      )}

      {/* Error */}
      {actionError && (
        <Box sx={{
          padding: "8px 20px",
          background: "rgba(200,64,64,0.08)",
          borderBottom: `1px solid ${t.border}`,
        }}>
          <Typography sx={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "11px", color: "#c84040",
          }}>
            {actionError}
          </Typography>
        </Box>
      )}

      {/* Buttons */}
      <Box sx={{ padding: "12px 20px", display: "flex", gap: "8px" }}>

        {/* Close always visible */}
        <Box
          onClick={onClose}
          sx={{
            padding: "8px 16px", borderRadius: "3px", cursor: "pointer",
            border: `1px solid ${t.border2}`,
            fontFamily: "'DM Mono', monospace", fontSize: "11px", color: t.text2,
            transition: "all 0.15s",
            "&:hover": { borderColor: t.border3, color: t.text },
          }}
        >
          Close
        </Box>

        {/* Reject button — only if this role can act */}
        {canAct && !showRejectForm && (
          <Box
            onClick={() => { setShowRejectForm(true); setActionError(null); }}
            sx={{
              padding: "8px 16px", borderRadius: "3px", cursor: "pointer",
              background: "rgba(200,64,64,0.08)",
              border: "1px solid rgba(200,64,64,0.25)",
              fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "#c84040",
              transition: "all 0.15s",
              "&:hover": { background: "rgba(200,64,64,0.16)" },
            }}
          >
            ✕ Reject
          </Box>
        )}

        {/* Reject form confirm/cancel */}
        {canAct && showRejectForm && (
          <>
            <Box
              onClick={() => { setShowRejectForm(false); setRejectComment(""); setActionError(null); }}
              sx={{
                padding: "8px 14px", borderRadius: "3px", cursor: "pointer",
                border: `1px solid ${t.border2}`,
                fontFamily: "'DM Mono', monospace", fontSize: "11px", color: t.text2,
                transition: "all 0.15s",
              }}
            >
              Cancel
            </Box>
            <Box
              onClick={actioning ? undefined : handleReject}
              sx={{
                flex: 1, padding: "8px 14px", borderRadius: "3px",
                cursor: actioning ? "not-allowed" : "pointer",
                background: actioning ? t.surface3 : "rgba(200,64,64,0.12)",
                border: "1px solid rgba(200,64,64,0.35)",
                fontFamily: "'DM Mono', monospace", fontSize: "11px", fontWeight: 600,
                color: actioning ? t.text3 : "#c84040",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                transition: "all 0.15s",
              }}
            >
              {actioning && <CircularProgress size={10} sx={{ color: "inherit" }} />}
              {actioning ? "Rejecting..." : "Confirm Rejection"}
            </Box>
          </>
        )}

        {/* Approve button — only if this role can act */}
        {canAct && !showRejectForm && (
          <Box
            onClick={actioning ? undefined : handleApprove}
            sx={{
              flex: 1, padding: "8px 14px", borderRadius: "3px",
              cursor: actioning ? "not-allowed" : "pointer",
              background: actioning ? t.surface3 : t.accent,
              fontFamily: "'DM Mono', monospace", fontSize: "11px", fontWeight: 600,
              color: actioning ? t.text3 : (isDark ? "#060E0D" : "#fff"),
              display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
              transition: "all 0.15s",
              "&:hover": actioning ? {} : { opacity: 0.88 },
            }}
          >
            {actioning && <CircularProgress size={10} sx={{ color: "inherit" }} />}
            {actioning ? "Processing..." : approveLabel}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ActionFooter;